import {
  Details,
  Reservation,
  Status,
  Reservee,
} from '@wix/ambassador-table-reservations-v1-reservation/types'
import {ControllerFlowAPI} from '@wix/yoshi-flow-editor'
import {zonedTimeToUtc} from '@wix/table-reservations-lib/timezone'
import {addHours} from 'date-fns'
import {
  PaymentPolicyType,
  ReservationLocation,
} from '@wix/ambassador-table-reservations-v1-reservation-location/types'
import {
  Experience,
  PaymentPolicyType as ExperiencePaymentPolicyType,
} from '@wix/ambassador-table-reservations-v1-experience/types'

import {getNearestQuarterTime} from '../../utils/date'
import {setDataToLocalStorage, getDataFromLocalStorage} from '../../utils/dataCapsule'
import {
  goToReservationConfirmation,
  goToReservationDetails,
  navigateToCheckoutPage,
} from '../../utils/navigation'
import {getLogger} from '../../utils/getLogger'
import {DEFAULT_PARTY_SIZE} from '../../utils/partySize'
import {RequestStatus} from '../../utils/wrapRequest'
import {DEFAULT_CURRENCY, TransactionNames} from '../../utils/constants'
import {ReservationDetailsStorage} from '../../components/ReservationDetails/storage'

import {EDITOR_MOCK_AREA_ID, EDITOR_MOCK_RESERVATION_LOCATION_ID} from './reservationLocation'
import {getExperienceMock} from './getExperienceMock'

export const RESERVATION_ID_MOCK = 'eb6fad5d-2f21-42d2-a330-84c4bb7df34d'

const CACHE_KEY = 'tr-reservation-mock'

export const getPreviewReservationPayment = (
  reservationLocation: ReservationLocation,
  partySize?: number | null,
) => {
  const paymentPolicy = reservationLocation.configuration?.paymentPolicy
  const isPerGuestPayment = paymentPolicy?.paymentPolicyType === PaymentPolicyType.PER_GUEST
  const options = isPerGuestPayment
    ? paymentPolicy?.perGuestOptions
    : paymentPolicy?.perReservationOptions
  const isPaymentRuleActive = !!(
    options?.minPartySize &&
    partySize &&
    partySize >= options.minPartySize
  )

  return {
    totalPrice: isPaymentRuleActive
      ? isPerGuestPayment
        ? (Number(options?.price) || 0) * (partySize || 0)
        : Number(options?.price) || 0
      : 0,
  }
}

export const getPreviewExperiencePayment = (
  experience: Experience,
  partySize?: number | null,
): {totalPrice: number} | undefined => {
  const paymentPolicy = experience.configuration?.paymentPolicy

  if (!paymentPolicy || paymentPolicy.paymentPolicyType === ExperiencePaymentPolicyType.FREE) {
    return undefined
  }

  const pricePerGuest = Number(paymentPolicy.perGuestOptions?.price) || 0

  return {totalPrice: pricePerGuest * (partySize || 0)}
}

interface PreviewPriceResult {
  totalPrice: number
  experience?: Experience
}

const getPreviewTotalPrice = async (
  flowAPI: ControllerFlowAPI,
  reservation: Reservation,
  reservationLocation: ReservationLocation,
): Promise<PreviewPriceResult> => {
  const partySize = reservation.details?.partySize

  if (reservation.details?.experienceId) {
    const experience = await getExperienceMock(flowAPI, reservation.details.experienceId)
    const experiencePayment = getPreviewExperiencePayment(experience, partySize)

    if (experiencePayment) {
      return {totalPrice: experiencePayment.totalPrice, experience}
    }
  }

  return {
    totalPrice: getPreviewReservationPayment(reservationLocation, partySize).totalPrice,
  }
}

const getPreviewCheckoutPayload = async (
  flowAPI: ControllerFlowAPI,
  reservation: Reservation,
  reservee: Reservee,
  reservationLocation: ReservationLocation,
) => {
  const locationName = reservationLocation.location?.name ?? undefined
  const {totalPrice, experience} = await getPreviewTotalPrice(
    flowAPI,
    reservation,
    reservationLocation,
  )
  const experienceName = experience?.configuration?.displayInfo?.name

  return {
    reservee,
    locationId: reservationLocation.location?.id ?? undefined,
    locationName,
    partySize: reservation.details?.partySize ?? undefined,
    startDate: reservation.details?.startDate?.toISOString?.(),
    price: String(totalPrice),
    currency: reservationLocation.currency || DEFAULT_CURRENCY,
    productName:
      experienceName ||
      (locationName ? `Table reservation at ${locationName}` : 'Table reservation'),
  }
}

const navigateToPreviewCheckout = async (
  flowAPI: ControllerFlowAPI,
  reservation: Reservation,
  reservee: Reservee,
  reservationLocation: ReservationLocation,
) => {
  const setProps = flowAPI.controllerConfig.setProps
  const {getResolvedError} = flowAPI.errorHandler
  const createCheckoutTransaction = flowAPI.panoramaClient.transaction(
    TransactionNames.createCheckout,
  )

  try {
    setProps({submitReservationStatus: RequestStatus.LOADING})

    const reservationFromCache = {
      ...reservation,
      reservee,
    }

    setDataToLocalStorage(flowAPI, CACHE_KEY, reservationFromCache)

    const previewCheckoutPayload = await getPreviewCheckoutPayload(
      flowAPI,
      reservationFromCache,
      reservee,
      reservationLocation,
    )

    createCheckoutTransaction.start({
      params: {
        isPreview: true,
        isCheckoutPreviewOOIEnabled: true,
        reservationId: reservationFromCache.id,
        reservationLocationId: reservationLocation.id,
        price: previewCheckoutPayload.price,
      },
    })

    const {checkoutService} = await import('../../services/checkoutService')
    const checkoutResponse = await checkoutService.createPreviewCheckout(
      flowAPI,
      previewCheckoutPayload,
    )

    if (!checkoutResponse?.checkout?.id) {
      throw new Error('Checkout is undefined')
    }

    createCheckoutTransaction.finish({
      checkoutId: checkoutResponse.checkout.id,
    })

    await navigateToCheckoutPage(flowAPI, checkoutResponse.checkout.id, RESERVATION_ID_MOCK)
  } catch (err: any) {
    flowAPI.panoramaClient.errorMonitor().reportError(err)
    createCheckoutTransaction.finish({
      error: true,
    })

    setProps({
      submitReservationStatus: RequestStatus.FAILED,
      apiRequestErrorDetails: getResolvedError(err),
    })
  }
}

const navigateToPreviewConfirmation = async (
  flowAPI: ControllerFlowAPI,
  reservation: Reservation,
  reservee: Reservee,
) => {
  const setProps = flowAPI.controllerConfig.setProps
  const {getResolvedError} = flowAPI.errorHandler

  try {
    setProps({submitReservationStatus: RequestStatus.LOADING})

    setDataToLocalStorage(flowAPI, CACHE_KEY, {
      ...reservation,
      reservee,
    })

    await goToReservationConfirmation({
      flowAPI,
      reservationId: RESERVATION_ID_MOCK,
      isTemplateView: true,
    })
  } catch (err: any) {
    flowAPI.panoramaClient.errorMonitor().reportError(err)

    setProps({
      submitReservationStatus: RequestStatus.FAILED,
      apiRequestErrorDetails: getResolvedError(err),
    })
  }
}

const createMockReservation = ({
  details,
  reservee,
  status,
}: {
  details: Details
  reservee: Reservee
  status: Status
}): Reservation => ({
  id: RESERVATION_ID_MOCK,
  status,
  reservee,
  details: {
    reservationLocationId: details.reservationLocationId,
    areaId: details.areaId,
    startDate: details.startDate,
    endDate: details.startDate ? addHours(details.startDate, 2) : undefined,
    partySize: details.partySize,
    experienceId: details.experienceId,
  },
})

export const handleReservationDataMock = (flowAPI: ControllerFlowAPI) => {
  return async ({
    details,
    timeZone,
    experienceId,
    isManualApproval,
  }: {
    details: Details
    timeZone?: string | null
    experienceId?: string
    isManualApproval?: boolean
  }) => {
    const resolvedExperienceId = experienceId ?? details.experienceId

    setDataToLocalStorage(flowAPI, CACHE_KEY, {
      id: RESERVATION_ID_MOCK,
      status: Status.HELD,
      details: {
        reservationLocationId: details.reservationLocationId,
        areaId: details.areaId,
        startDate: zonedTimeToUtc(details.startDate!, timeZone),
        endDate: addHours(zonedTimeToUtc(details.startDate!, timeZone), 2),
        partySize: details.partySize,
        experienceId: resolvedExperienceId,
      },
    })

    if (isManualApproval) {
      return goToReservationDetails({
        flowAPI,
        timeZone,
        reservationData: {
          startDate: zonedTimeToUtc(details.startDate!, timeZone),
          partySize: details.partySize!,
          reservationLocationId: details.reservationLocationId!,
          areaId: details.areaId,
        },
        experienceId: resolvedExperienceId!,
      })
    }
    await goToReservationDetails({
      flowAPI,
      isTemplateView: true,
      reservationId: RESERVATION_ID_MOCK,
    })
  }
}

const submitPreviewReservation = async (
  flowAPI: ControllerFlowAPI,
  reservation: Reservation,
  reservee: Reservee,
  reservationLocation: ReservationLocation,
) => {
  const logger = getLogger(flowAPI.bi!)

  logger.reservationCreated({
    isPreview: true,
    reservation,
    reservee,
    reservationLocation,
    isCardTokenization: false,
  })

  const {totalPrice} = await getPreviewTotalPrice(flowAPI, reservation, reservationLocation)

  if (totalPrice > 0) {
    await navigateToPreviewCheckout(flowAPI, reservation, reservee, reservationLocation)
  } else {
    await navigateToPreviewConfirmation(flowAPI, reservation, reservee)
  }
}

export const reserveReservationMock = (flowAPI: ControllerFlowAPI) => {
  return async (
    reservation: Reservation,
    reservee: Reservee,
    reservationLocation: ReservationLocation,
  ) => {
    const reservationFromCache = getReservationMock(flowAPI, Status.RESERVED)

    await submitPreviewReservation(flowAPI, reservationFromCache, reservee, reservationLocation)
  }
}

export const createReservationMock = (flowAPI: ControllerFlowAPI) => {
  return async (
    details: Details,
    reservee: Reservee,
    reservationLocation: ReservationLocation,
    _paymentTokenizationSupported: boolean,
  ) => {
    const reservation = createMockReservation({
      details,
      reservee,
      status: Status.REQUESTED,
    })

    await submitPreviewReservation(flowAPI, reservation, reservee, reservationLocation)
  }
}

export const getReservationMock = (
  flowAPI: ControllerFlowAPI,
  status?: Status,
  fallbackReservationLocationId?: string,
): Reservation => {
  const reservationFromCache = getDataFromLocalStorage<Reservation>(flowAPI, CACHE_KEY)

  if (reservationFromCache) {
    return {
      ...reservationFromCache,
      status: status ?? reservationFromCache.status,
      details: {
        ...reservationFromCache.details,
        startDate: new Date(reservationFromCache.details!.startDate!),
        endDate: new Date(reservationFromCache.details!.endDate!),
      },
    }
  } else {
    const date = getNearestQuarterTime(new Date())

    return {
      id: RESERVATION_ID_MOCK,
      status: status ?? Status.HELD,
      details: {
        reservationLocationId: fallbackReservationLocationId ?? EDITOR_MOCK_RESERVATION_LOCATION_ID,
        areaId: EDITOR_MOCK_AREA_ID,
        startDate: date,
        endDate: date,
        partySize: DEFAULT_PARTY_SIZE,
      },
    }
  }
}

export const getReservationDataQueryParamsMock = (
  flowAPI: ControllerFlowAPI,
  fallbackReservationLocationId?: string,
): ReservationDetailsStorage['reservationDataQueryParams'] => {
  const query = flowAPI.controllerConfig?.wixCodeApi?.location?.query

  return {
    startDate: query?.startDate || new Date().toISOString(),
    partySize: query?.partySize || DEFAULT_PARTY_SIZE.toString(),
    reservationLocationId: query?.reservationLocationId || fallbackReservationLocationId,
    areaId: query?.areaId || EDITOR_MOCK_AREA_ID,
    experienceIdQueryParam: query?.experienceId,
  }
}
